import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Virtual Support Team Productivity and SLA Software | TeamGrid";
const SEO_DESCRIPTION = "Improve virtual support productivity, SLA compliance and quality consistency with real time performance and collaboration insights.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/virtual-support-services";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const VirtualSupportServices = () => {
  const navigate = useNavigate();
  // FAQ data specific to Virtual Support Services
  const virtualSupportFAQs = [
    {
      question: "Does TeamGrid show support SLA performance?",
      answer:
        "Yes, TeamGrid tracks response and resolution patterns that help improve support SLA performance over time.",
    },
    {
      question:
        "Can it track response and resolution times without monitoring?",
      answer:
        "Yes, it analyzes task and workflow data to highlight response trends without intrusive monitoring.",
    },
    {
      question: "Is TeamGrid suitable for remote support teams?",
      answer:
        "Yes, TeamGrid helps remote support teams improve productivity and maintain high performance in distributed environments.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Does TeamGrid show support SLA performance?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid tracks response and resolution patterns to help teams improve support SLA performance over time." } },
            { "@type": "Question", name: "Can TeamGrid track response and resolution times without monitoring?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid analyzes task and workflow data to surface response trends without intrusive monitoring." } },
            { "@type": "Question", name: "Is TeamGrid suitable for remote support teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid helps remote support teams maintain productivity and performance in distributed environments." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="Virtual Support Services"
      heroTitle="Support Team Productivity & <span style='color: #00D1C1; font-weight: 600;'>SLA Performance Software</span>"
      heroSubtitle="Deliver Fast, Reliable Support—Without Losing Visibility or Quality"
      primaryButtonText="Get a Demo for Your Support Team"
      customPrimaryButton={customButton}
      fullSolutionContentTitle="Scale Virtual Support Operations"
      fullSolutionContentDescription="TeamGrid helps high-performing virtual support teams stay responsive, productive, and aligned through productivity assurance, response time tracking, quality consistency monitoring, and team cohesion analysis."
      fullSolutionContentOutcomes="Achieve 28% improvement in remote support productivity, 40% improvement in SLA compliance, 35% lower quality variation across agents, 32% improvement in collaboration, and 30%+ reduction in turnover."
      hideProductivity={true}
      hideFeatures={true}
      hideOutcomes={true}
      hideWhyChoose={true}
        faqs={virtualSupportFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for virtual support services."
      />
    </>
  );
};

export default VirtualSupportServices;
